import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { AdminTenant } from '@terafile/shared'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { UpdateTenantPlanModal } from '../components/UpdateTenantPlanModal.js'
import { listAdminTenants, suspendTenant, unsuspendTenant } from '../lib/admin-api.js'
import { FETCH_ERROR_FALLBACK, toUserMessage } from '../lib/error-copy.js'
import { LOADING, PLAN_LABEL, TENANT_STATUS_LABEL } from '../lib/labels.js'

const PAGE_LIMIT = 50

const PLAN_OPTIONS = [
  { value: '', label: 'すべて' },
  { value: 'FREE', label: PLAN_LABEL.FREE },
  { value: 'PRO', label: PLAN_LABEL.PRO },
  { value: 'EPHEMERAL', label: PLAN_LABEL.EPHEMERAL },
] as const

const STATUS_OPTIONS = [
  { value: '', label: 'すべて' },
  { value: 'ACTIVE', label: TENANT_STATUS_LABEL.ACTIVE },
  { value: 'SUSPENDED', label: TENANT_STATUS_LABEL.SUSPENDED },
  { value: 'FROZEN', label: TENANT_STATUS_LABEL.FROZEN },
] as const

/**
 * 管理者向けテナント一覧（エディトリアル × 司令室）。
 * AdminDashboard と意匠統一。
 */
export function AdminTenantsPage() {
  const queryClient = useQueryClient()
  const [planFilter, setPlanFilter] = useState<'' | 'FREE' | 'PRO' | 'EPHEMERAL'>('')
  const [statusFilter, setStatusFilter] = useState<'' | 'ACTIVE' | 'SUSPENDED' | 'FROZEN'>('')
  // 検索ボックスの即時値（input controlled） + debounce 後にクエリへ反映する値
  const [searchInput, setSearchInput] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [page, setPage] = useState(1)
  const [planTarget, setPlanTarget] = useState<AdminTenant | null>(null)

  // 入力 → 300ms 待ってからクエリ反映（短時間連打で API を叩きすぎないため）
  useEffect(() => {
    const id = setTimeout(() => setSearchQuery(searchInput.trim()), 300)
    return () => clearTimeout(id)
  }, [searchInput])

  // フィルタ・検索が変わったら page を 1 に戻す（古い page を保持して空ページになるのを防ぐ）
  // biome-ignore lint/correctness/useExhaustiveDependencies: page 自身は意図的に依存しない
  useEffect(() => {
    setPage(1)
  }, [planFilter, statusFilter, searchQuery])

  const { data, isLoading, error, isPlaceholderData } = useQuery({
    queryKey: ['admin-tenants', planFilter, statusFilter, searchQuery, page],
    queryFn: () =>
      listAdminTenants({
        ...(planFilter ? { plan: planFilter } : {}),
        ...(statusFilter ? { status: statusFilter } : {}),
        ...(searchQuery ? { q: searchQuery } : {}),
        page,
        limit: PAGE_LIMIT,
      }),
    placeholderData: (prev) => prev, // ページ送り時にちらつかせない
  })

  const total = data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / PAGE_LIMIT))

  const suspendMut = useMutation({
    mutationFn: (id: string) => suspendTenant(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-tenants'] }),
  })
  const unsuspendMut = useMutation({
    mutationFn: (id: string) => unsuspendTenant(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-tenants'] }),
  })

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-10 py-10 md:py-16 space-y-10 grain">
      <Hero
        total={data?.total ?? null}
        shownStart={data && data.items.length > 0 ? (page - 1) * PAGE_LIMIT + 1 : 0}
        shownEnd={data ? (page - 1) * PAGE_LIMIT + data.items.length : 0}
      />

      <Toolbar
        planFilter={planFilter}
        setPlanFilter={(v) => setPlanFilter(v as typeof planFilter)}
        statusFilter={statusFilter}
        setStatusFilter={(v) => setStatusFilter(v as typeof statusFilter)}
        searchInput={searchInput}
        setSearchInput={setSearchInput}
      />

      {isLoading && <p className="text-sm text-muted-foreground">{LOADING.fetching}</p>}
      {error && (
        <div className="border border-destructive/40 bg-destructive/5 rounded-md p-4 text-sm text-destructive">
          {toUserMessage(error, FETCH_ERROR_FALLBACK)}
        </div>
      )}

      {data && data.items.length === 0 && (
        <div className="border border-dashed border-border rounded-lg py-12 text-center bg-muted/20">
          <p className="text-sm text-muted-foreground">
            {searchQuery
              ? `「${searchQuery}」に一致するテナントはありません`
              : '該当するテナントはありません'}
          </p>
        </div>
      )}

      {data && data.items.length > 0 && (
        <>
          <ul
            className={`divide-y divide-border border-y border-border ${
              isPlaceholderData ? 'opacity-60' : ''
            }`}
          >
            {data.items.map((t, i) => (
              <TenantRow
                key={t.id}
                tenant={t}
                index={(page - 1) * PAGE_LIMIT + i + 1}
                onPlan={() => setPlanTarget(t)}
                onSuspend={() => suspendMut.mutate(t.id)}
                onUnsuspend={() => unsuspendMut.mutate(t.id)}
                pending={suspendMut.isPending || unsuspendMut.isPending}
              />
            ))}
          </ul>

          <Pagination
            page={page}
            totalPages={totalPages}
            setPage={setPage}
            busy={isPlaceholderData}
          />
        </>
      )}

      <UpdateTenantPlanModal tenant={planTarget} onClose={() => setPlanTarget(null)} />
    </div>
  )
}

// ===========================================================================
// 視覚部品
// ===========================================================================

function Hero({
  total,
  shownStart,
  shownEnd,
}: {
  total: number | null
  shownStart: number
  shownEnd: number
}) {
  return (
    <section>
      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground/80 mb-3">
        Operations · Tenants
      </p>
      <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold leading-[1.05] tracking-tight">
        テナント一覧
      </h1>
      {total !== null && (
        <p className="mt-4 text-sm text-muted-foreground">
          全{' '}
          <span className="font-medium text-foreground editorial-num">
            {total.toLocaleString('ja-JP')}
          </span>{' '}
          件
          {total > 0 && (
            <>
              {' '}
              中{' '}
              <span className="font-medium text-foreground editorial-num">
                {shownStart.toLocaleString('ja-JP')}–{shownEnd.toLocaleString('ja-JP')}
              </span>{' '}
              を表示
            </>
          )}
        </p>
      )}
    </section>
  )
}

function Toolbar({
  planFilter,
  setPlanFilter,
  statusFilter,
  setStatusFilter,
  searchInput,
  setSearchInput,
}: {
  planFilter: string
  setPlanFilter: (v: string) => void
  statusFilter: string
  setStatusFilter: (v: string) => void
  searchInput: string
  setSearchInput: (v: string) => void
}) {
  return (
    <div className="flex items-center gap-4 flex-wrap border-b border-border pb-3">
      <div className="flex items-center gap-2 flex-1 min-w-[240px]">
        <label
          htmlFor="search"
          className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground font-mono"
        >
          検索
        </label>
        <input
          id="search"
          type="search"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="テナント名・メールアドレスの部分一致"
          className="flex-1 px-3 py-1.5 border border-border rounded-md text-sm bg-background"
        />
      </div>
      <FilterSelect
        id="plan"
        label="プラン"
        value={planFilter}
        options={PLAN_OPTIONS}
        onChange={setPlanFilter}
      />
      <FilterSelect
        id="status"
        label="ステータス"
        value={statusFilter}
        options={STATUS_OPTIONS}
        onChange={setStatusFilter}
      />
    </div>
  )
}

function Pagination({
  page,
  totalPages,
  setPage,
  busy,
}: {
  page: number
  totalPages: number
  setPage: (n: number) => void
  busy: boolean
}) {
  if (totalPages <= 1) return null
  return (
    <nav aria-label="ページネーション" className="flex items-center justify-between gap-3 pt-2">
      <button
        type="button"
        onClick={() => setPage(Math.max(1, page - 1))}
        disabled={page === 1 || busy}
        className="px-3 py-1.5 border border-border rounded-md text-sm hover:bg-accent transition-colors disabled:opacity-40"
      >
        ← 前へ
      </button>
      <span className="text-sm text-muted-foreground font-mono editorial-num">
        {page} / {totalPages}
      </span>
      <button
        type="button"
        onClick={() => setPage(Math.min(totalPages, page + 1))}
        disabled={page === totalPages || busy}
        className="px-3 py-1.5 border border-border rounded-md text-sm hover:bg-accent transition-colors disabled:opacity-40"
      >
        次へ →
      </button>
    </nav>
  )
}

function FilterSelect({
  id,
  label,
  value,
  options,
  onChange,
}: {
  id: string
  label: string
  value: string
  options: readonly { value: string; label: string }[]
  onChange: (v: string) => void
}) {
  return (
    <div className="flex items-center gap-2">
      <label
        htmlFor={id}
        className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground font-mono"
      >
        {label}
      </label>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="px-3 py-1.5 border border-border rounded-md text-sm bg-background hover:bg-accent transition-colors cursor-pointer"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  )
}

function TenantRow({
  tenant,
  index,
  onPlan,
  onSuspend,
  onUnsuspend,
  pending,
}: {
  tenant: AdminTenant
  index: number
  onPlan: () => void
  onSuspend: () => void
  onUnsuspend: () => void
  pending: boolean
}) {
  return (
    // スマホは Link → 操作行の縦積み、md+ は Link（右に操作ボタン用の余白）+ absolute ボタン。
    // 旧実装は常に pr-56 + absolute だったので、狭い画面で本文が 224px 潰されていた。
    <li className="group relative flex flex-col md:block">
      <Link
        to={`/admin/tenants/${tenant.id}`}
        className="flex items-stretch gap-4 md:gap-6 py-5 pl-2 md:pl-4 pr-2 md:pr-56 hover:bg-accent/40 transition-colors"
        aria-label={`${tenant.name ?? '(無名)'} の詳細`}
      >
        <span
          aria-hidden
          className="font-mono text-xs editorial-num text-muted-foreground/40 group-hover:text-muted-foreground/80 pt-1 w-6 flex-shrink-0 transition-colors"
        >
          {String(index).padStart(2, '0')}
        </span>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="font-display text-lg md:text-xl font-medium tracking-tight truncate group-hover:underline underline-offset-4 decoration-foreground/30">
              {tenant.name ?? '(無名)'}
            </h2>
            <StatusBadge status={tenant.status} />
            <PlanBadge plan={tenant.plan} kind={tenant.kind} />
          </div>
          {/* 代表メアド: 「誰のテナントか」を識別する一次情報。lastLogin 降順で最初の人 */}
          <OwnerLine tenant={tenant} />
          <div className="mt-1.5 flex items-center gap-3 text-xs text-muted-foreground font-mono editorial-num flex-wrap">
            <span>{formatBytes(Number(tenant.storageUsedBytes))}</span>
            <span aria-hidden className="text-muted-foreground/40">
              ·
            </span>
            <span>作成 {new Date(tenant.createdAt).toLocaleDateString('ja-JP')}</span>
          </div>
        </div>
      </Link>

      {/* 操作群: スマホは Link の下に横並び、md+ は右端に重ねる */}
      <div className="flex items-center gap-2 px-2 pb-4 pl-10 md:pl-0 md:pb-0 md:absolute md:right-4 md:top-1/2 md:-translate-y-1/2">
        {tenant.kind === 'REGULAR' && (
          <button
            type="button"
            onClick={onPlan}
            className="text-xs px-2.5 py-1.5 border border-border rounded-md hover:bg-background transition-colors"
          >
            プラン
          </button>
        )}
        {tenant.status === 'ACTIVE' ? (
          <button
            type="button"
            onClick={onSuspend}
            disabled={pending}
            className="text-xs px-2.5 py-1.5 border border-destructive/30 text-destructive rounded-md hover:bg-destructive/5 transition-colors disabled:opacity-50"
          >
            停止
          </button>
        ) : (
          <button
            type="button"
            onClick={onUnsuspend}
            disabled={pending}
            className="text-xs px-2.5 py-1.5 border border-border rounded-md hover:bg-background transition-colors disabled:opacity-50"
          >
            解除
          </button>
        )}
      </div>
    </li>
  )
}

/**
 * テナントの代表所属ユーザー（メールアドレス）行。
 *
 * - REGULAR: API が lastLoginAt 降順で最大 3 件返してくる。先頭を強調表示し、
 *   2 件目以降は「+N 人」で省略
 * - EPHEMERAL: members は空配列。`(暫定: ユーザー無し)` を出して識別性を保つ
 */
function OwnerLine({ tenant }: { tenant: AdminTenant }) {
  if (tenant.kind === 'EPHEMERAL') {
    return (
      <p className="mt-1 text-xs text-muted-foreground italic">(暫定テナント · ユーザー登録なし)</p>
    )
  }
  if (tenant.members.length === 0) {
    return <p className="mt-1 text-xs text-amber-700">所属ユーザー無し</p>
  }
  const primary = tenant.members[0]
  if (!primary) return null
  const others = tenant.memberCount - 1
  return (
    <p className="mt-1 text-xs text-muted-foreground truncate">
      <span className="text-foreground font-medium">{primary.email}</span>
      {primary.isAdmin && (
        <span className="ml-1.5 text-[10px] uppercase tracking-wider text-destructive">admin</span>
      )}
      {others > 0 && (
        <span className="ml-2 text-muted-foreground">ほか {others.toLocaleString('ja-JP')} 人</span>
      )}
    </p>
  )
}

function StatusBadge({ status }: { status: AdminTenant['status'] }) {
  // ラベルは labels.ts の TENANT_STATUS_LABEL を共通参照（issue #36）
  const config: Record<AdminTenant['status'], { className: string }> = {
    ACTIVE: { className: 'border-emerald-300 text-emerald-800 bg-emerald-50' },
    SUSPENDED: { className: 'border-destructive/30 text-destructive bg-destructive/5' },
    FROZEN: { className: 'border-amber-300 text-amber-800 bg-amber-50' },
  }
  const { className } = config[status]
  return (
    <span
      className={`text-[10px] uppercase tracking-wider px-2 py-0.5 border rounded-full font-medium ${className}`}
    >
      {TENANT_STATUS_LABEL[status]}
    </span>
  )
}

function PlanBadge({
  plan,
  kind,
}: {
  plan: AdminTenant['plan']
  kind: AdminTenant['kind']
}) {
  // EPHEMERAL kind は plan より優先（暫定テナントは plan=EPHEMERAL なので結果同じだが意図を明示）
  const label = kind === 'EPHEMERAL' ? PLAN_LABEL.EPHEMERAL : PLAN_LABEL[plan]
  return (
    <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 border border-border rounded-full text-muted-foreground">
      {label}
    </span>
  )
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  if (bytes < 1024 ** 4) return `${(bytes / 1024 ** 3).toFixed(2)} GB`
  return `${(bytes / 1024 ** 4).toFixed(2)} TB`
}
